import { Button } from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { EnumTipoFacturacion } from '../../../api'
import { InvoiceCycleProcessingModel } from '../../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import { ActiveSettlementBatchModel } from '../../../api/Argentia/endpoints/settlementBatchs/model/settlementBatchs'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import { EnumEstadoLoteLiquidacion } from '../../../api/oData/enums/enumEstadoLoteLiquidacion'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { NonAuthRoutes } from '../../../model'
import { ServiceResponse } from '../../../model/serviceResponse'
import {
  addInvoiceCycleProcess,
  removeActiveProcess
} from '../../../redux/actions/processing.actions'
import { selectActiveProcesses } from '../../../redux/selectors/processing.selectors'
import { getActiveSettlementBatchs } from '../../settlementBatchs/settlementBatchs.services'
import { useInvoiceCyclePolling } from '../hooks/useInvoiceCyclePolling'
import { fetchInvoiceCycleExecution, fetchInvoiceCycleProcessing } from '../invoiceCycles.service'
import { InvoiceCycleBatches } from './components/invoiceCycleBatches'
import { InvoiceCycleProcessingProgress } from './components/invoiceCycleProcessingProgress'
import { invoiceCycleStatusConfig } from './invoiceCycleStatusConfig'

/**
 * Determina qué lote se muestra con barra de progreso:
 * - Si el lote en foco sigue activo, se mantiene
 * - Si no, el En curso más antiguo (mismo criterio que el BE sin batchId)
 * - Si no hay En curso, el primero de la lista (o ninguno)
 */
const resolveFocusedBatchId = (
  batches: ActiveSettlementBatchModel[],
  currentFocusedBatchId?: number
): number | undefined => {
  if (batches.some(batch => batch.id === currentFocusedBatchId)) {
    return currentFocusedBatchId
  }
  const inProgressBatches = batches
    .filter(batch => batch.status === EnumEstadoLoteLiquidacion.EnCurso)
    .sort((a, b) => a.id - b.id)
  return inProgressBatches[0]?.id ?? batches[0]?.id
}

interface InvoiceCyclesPageProps {
  location?: {
    state?: {
      executionCycleId?: number
      invoiceCycleId?: number
      originState?: EnumEstadoCiclo
      [key: string]: any
    }
  }
}

export const InvoiceCyclesProcessingPage: React.FC<InvoiceCyclesPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'invoiceCyclesProcessingPage'])
  const history = useHistory()
  const dispatch = useDispatch()
  const activeProcesses = useSelector(selectActiveProcesses)

  const executionCycleId = props.location?.state?.executionCycleId
  const cycleName = props.location?.state?.cycleName
  const originState = props.location?.state?.originState
  const additionalState = props.location?.state

  const [currentStatus, setCurrentStatus] = useState<EnumEstadoCiclo>(EnumEstadoCiclo.Procesando)
  const [processingProgress, setProcessingProgress] = useState<InvoiceCycleProcessingModel>({
    processedElements: 0,
    totalProcessingElements: 0
  })
  // false hasta que termina el primer fetch del polling: mientras tanto se muestran skeletons
  const [hasFetchedOnce, setHasFetchedOnce] = useState<boolean>(false)
  const [batches, setBatches] = useState<ActiveSettlementBatchModel[]>([])
  const [focusedBatchId, setFocusedBatchId] = useState<number | undefined>(undefined)
  // Ref para que el polling siempre lea el lote en foco más reciente
  const focusedBatchIdRef = useRef<number | undefined>(undefined)

  const isRefacturacionOAnuladora =
    additionalState?.billingType === EnumTipoFacturacion.Anuladora ||
    additionalState?.billingType === EnumTipoFacturacion.Refacturacion

  // #region Handlers Starts

  /**
   * Función que consulta el estado actual del ciclo de ejecución y el progreso en paralelo
   */
  const fetchCycleStatus = async (): Promise<EnumEstadoCiclo> => {
    if (!executionCycleId) {
      console.error('No executionCycleId provided')
      return currentStatus
    }

    try {
      // Ejecutar llamada de ejecución
      const executionResponse = await fetchInvoiceCycleExecution(executionCycleId)

      // Ejecutar llamadas de lotes y progreso solo si no es Refacturación/Anuladora
      let progressResponse: ServiceResponse<InvoiceCycleProcessingModel | undefined> | undefined
      if (!isRefacturacionOAnuladora) {
        const batchesResponse = await getActiveSettlementBatchs(executionCycleId)
        const activeBatches =
          batchesResponse.success && batchesResponse.data ? batchesResponse.data : []
        setBatches(activeBatches)

        // El lote en foco se mantiene mientras siga activo; si no, se toma el En curso más antiguo
        const nextFocusedBatchId = resolveFocusedBatchId(activeBatches, focusedBatchIdRef.current)
        focusedBatchIdRef.current = nextFocusedBatchId
        setFocusedBatchId(nextFocusedBatchId)

        progressResponse = await fetchInvoiceCycleProcessing(executionCycleId, nextFocusedBatchId)
      }

      // Actualizar estado del ciclo
      if (executionResponse.success && executionResponse.data) {
        const newStatus = executionResponse.data.status as EnumEstadoCiclo

        // No actualizar currentStatus si el BE aún está en el estado de origen
        // (el proceso todavía no arrancó — mantener Procesando en la UI)
        if (!originState || newStatus !== originState) {
          setCurrentStatus(newStatus)
        }

        // Actualizar progreso si está disponible
        if (progressResponse?.success && progressResponse?.data) {
          setProcessingProgress(progressResponse.data)
        }

        return newStatus
      }
      return currentStatus
    } catch (error) {
      console.error('Error fetching cycle status:', error)
      throw error
    } finally {
      setHasFetchedOnce(true)
    }
  }

  const { pollingState, resumePolling, restartPolling, cancelPolling } = useInvoiceCyclePolling(
    executionCycleId!,
    currentStatus,
    fetchCycleStatus,
    additionalState,
    originState
    // Sin disableAutoNavigation - navega automáticamente cuando termina
  )

  const statusConfig = invoiceCycleStatusConfig[currentStatus]

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    // Si sigue procesando, el seguimiento pasa a Redux para continuar en segundo plano
    if (executionCycleId && statusConfig.isProcessing) {
      dispatch(addInvoiceCycleProcess(executionCycleId, currentStatus, additionalState, cycleName))
      // Detener el polling local porque ahora se manejará en la notificación flotante
      cancelPolling()
    }
    history.push(NonAuthRoutes.invoiceCyclesNew)
  }

  const handleViewBatchProgressClick = (batchId: number) => {
    // El polling siguiente consultará el progreso de este lote
    focusedBatchIdRef.current = batchId
    setFocusedBatchId(batchId)
    setProcessingProgress({ processedElements: 0, totalProcessingElements: 0 })
    // Nuevo objetivo de polling: contador de intentos a 0 y consulta inmediata
    restartPolling()
  }

  const handleValidateInvoicesButtonClick = () => {
    history.push(NonAuthRoutes.SettlementsInvoices, { executionCycleId, ...additionalState })
  }

  const handleReviewSettlementsButtonClick = () => {
    history.push(NonAuthRoutes.Settlements, { executionCycleId, ...additionalState })
  }
  // #endregion Handlers Ends

  // #region Effects

  useEffect(() => {
    // Validar que tenemos el ID necesario
    if (!executionCycleId) {
      console.error('No execution cycle ID provided, redirecting to cycles list')
      history.push(NonAuthRoutes.invoiceCyclesNew)
    }
  }, [executionCycleId])

  // Esta pantalla hace su propio seguimiento: se retira el mini-layer de la ejecución (evita duplicar el polling)
  useEffect(() => {
    activeProcesses.forEach(process => {
      if (process.processType === 'invoiceCycle' && process.executionCycleId === executionCycleId) {
        dispatch(removeActiveProcess(process.processId))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #endregion Effects

  const waitingContent = (
    <InvoiceCycleProcessingProgress
      isProcessing={statusConfig.isProcessing}
      pollingState={pollingState}
      onResume={resumePolling}
      onBack={handleBackButtonClick}
      processedElements={processingProgress.processedElements}
      totalProcessingElements={processingProgress.totalProcessingElements}
    />
  )

  return (
    <Page layout={PageLayout.MULTI_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <PageHeader
          title={t(statusConfig.i18nKey)}
          subtitle={t('invoiceCyclesProcessingPage:header.subtitle')}
        >
          <Button onClick={handleBackButtonClick} variant="text" color="primary" size="small">
            {t('invoiceCyclesProcessingPage:buttons.backToList')}
          </Button>
          <Button
            onClick={handleValidateInvoicesButtonClick}
            variant="text"
            color="primary"
            size="small"
          >
            {t('invoiceCyclesProcessingPage:buttons.validateInvoices')}
          </Button>
          <Button
            onClick={handleReviewSettlementsButtonClick}
            variant="text"
            color="primary"
            size="small"
          >
            {t('invoiceCyclesProcessingPage:buttons.reviewSettlements')}
          </Button>
        </PageHeader>

        {/* MAIN BODY COMPONENTS */}
        {(!hasFetchedOnce || batches.length > 0) && (
          <InvoiceCycleBatches
            isLoading={!hasFetchedOnce}
            batches={batches}
            focusedBatchId={focusedBatchId}
            processedElements={processingProgress.processedElements}
            totalProcessingElements={processingProgress.totalProcessingElements}
            onViewProgress={handleViewBatchProgressClick}
            // Con el polling pausado, el componente de pausa reemplaza la card del lote en curso
            pausedContent={pollingState.isPaused ? waitingContent : undefined}
            pollingAttempt={{ current: pollingState.currentRetry, max: pollingState.maxRetries }}
          />
        )}
        {/* Sin lotes todavía (el lote aún no se creó): estado de espera */}
        {hasFetchedOnce && batches.length === 0 && waitingContent}
      </PageCenterColumn>
    </Page>
  )
}
